/**
 * @file This file contains the configuration for all settlement buildings in the game.
 * @module settlementsConfig
 */

export const settlements = {
    // --- Tier 1: Basic Resource Gathering ---
    'settlement_lake': {
        key: 'settlement_lake',
        display: { background: '🏞️', foreground: '🐟' },
        name: 'Lake',
        style: { backgroundColor: '#3B82F6' },
        isStationary: true,
        recipes: [
            { imports: { 'accessory_fishing_pole': 1 }, exports: { 'accessory_fish': 3 } },
            { imports: { 'accessory_bucket': 1 }, exports: { 'accessory_water': 3 } }
        ],
        idleIntervalInTicks: 5
    },
    'settlement_forest': {
        key: 'settlement_forest',
        display: { background: '🌳', foreground: '🪓' },
        name: 'Forest',
        style: { backgroundColor: '#166534' }, // Dark Green
        isStationary: true,
        recipes: [{ imports: { 'accessory_axe': 1 }, exports: { 'accessory_wood': 3 } }],
        idleIntervalInTicks: 10
    },
    'settlement_quarry': {
        key: 'settlement_quarry',
        display: { background: '⛰️', foreground: '⛏️' },
        name: 'Quarry',
        style: { backgroundColor: '#57534E' }, // Stone Gray
        isStationary: true,
        recipes: [
            { imports: { 'accessory_pickaxe': 1 }, exports: { 'accessory_stone': 5 } },
            { imports: { 'accessory_hammer': 1 }, exports: { 'accessory_stone': 1 } },
            { imports: { 'accessory_pickaxe': 1 }, exports: { 'accessory_gem': 1 } }
        ],
        idleIntervalInTicks: 10
    },
    // --- Tier 2: Basic Processing & Toolmaking ---
    'settlement_sawmill': {
        key: 'settlement_sawmill',
        display: { background: '🏭', foreground: '<img src="assets/images/png/log.png" alt="Saw" />' },
        name: 'Sawmill',
        style: { backgroundColor: '#A16207' }, // Brown
        isStationary: true,
        recipes: [{ imports: { 'accessory_wood': 1 }, exports: { 'accessory_plank': 5 } }],
        idleIntervalInTicks: 12
    },
    'settlement_blacksmith': {
        key: 'settlement_blacksmith',
        display: { background: '🏭', foreground: '🔨' },
        name: 'Blacksmith',
        style: { backgroundColor: '#4B5563' }, // Dark Gray
        isStationary: true,
        recipes: [
            { imports: { 'accessory_wood': 1, 'accessory_stone': 1 }, exports: { 'accessory_hammer': 5 } },
            { imports: { 'accessory_plank': 1, 'accessory_stone': 2 }, exports: { 'accessory_wrench': 1 } },
            { imports: { 'accessory_plank': 3, 'accessory_stone': 1 }, exports: { 'accessory_shield': 1 } },
            { imports: { 'accessory_stone': 2 }, exports: { 'accessory_spoon': 1 } },
            { imports: { 'accessory_stone': 2 }, exports: { 'accessory_kitchen_knife': 1 } }
        ],
        idleIntervalInTicks: 20
    },
    'settlement_stonemason': {
        key: 'settlement_stonemason',
        display: { background: '🗿', foreground: '🔨' },
        name: 'Stonemason',
        style: { backgroundColor: '#78716C' }, // Lighter Stone Gray
        isStationary: true,
        recipes: [
            { imports: { 'accessory_stone': 2, 'accessory_hammer': 1 }, exports: { 'accessory_pickaxe': 5 } },
            { imports: { 'accessory_moyai': 1 }, exports: { 'accessory_stone': 5 } }
        ],
        idleIntervalInTicks: 20
    },
    // --- Tier 3: Intermediate Goods Production ---
    'settlement_farm': {
        key: 'settlement_farm',
        display: { background: '🏡', foreground: '🌾' },
        name: 'Farm',
        style: { backgroundColor: '#84CC16' },
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_wheat': 5 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_corn': 5 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_chestnut': 5 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_peanuts': 5 } }
        ]
    },
    'settlement_fruit_farm': {
        key: 'settlement_fruit_farm',
        display: { background: '🏡', foreground: '🍎' },
        name: 'Fruit Farm',
        style: { backgroundColor: '#BE123C' }, // Rose color
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_apple': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_strawberry': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_pear': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_kiwi_fruit': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_watermelon': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_banana': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_cherries': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_peach': 3 } }
        ],
        idleIntervalInTicks: 12
    },
    'settlement_vegetable_farm': {
        key: 'settlement_vegetable_farm',
        display: { background: '🏡', foreground: '🥕' },
        name: 'Vegetable Farm',
        style: { backgroundColor: '#16A34A' }, // Green
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_potato': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_onion': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_garlic': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_cucumber': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_hot_pepper': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_avocado': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_tomato': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_carrot': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_eggplant': 3 } },
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_mushroom': 3 } }
        ],
        idleIntervalInTicks: 12
    },
    'settlement_ranch': {
        key: 'settlement_ranch',
        display: { background: '🏡', foreground: '🐄' },
        name: 'Ranch',
        style: { backgroundColor: '#78350F' }, // Brown
        isStationary: true,
        recipes: [
            { imports: { 'accessory_wheat': 2 }, exports: { 'accessory_meat': 5 } },
            { imports: { 'accessory_wheat': 2 }, exports: { 'accessory_milk': 5 } },
            { imports: { 'accessory_flower': 2 }, exports: { 'accessory_honey': 5 } }
        ],
        idleIntervalInTicks: 15
    },
    'settlement_chicken_farm': {
        key: 'settlement_chicken_farm',
        display: { background: '🏡', foreground: '🐔' },
        name: 'Chicken Farm',
        style: { backgroundColor: '#FDE68A' }, // Light Yellow
        isStationary: true,
        recipes: [{ imports: { 'accessory_wheat': 2 }, exports: { 'accessory_egg': 5 } }],
        idleIntervalInTicks: 10
    },
    'settlement_loom': {
        key: 'settlement_loom',
        display: { background: '🏠', foreground: '🧵' },
        name: 'Loom',
        style: { backgroundColor: '#D1D5DB' }, // Gray-300
        isStationary: true,
        recipes: [{ imports: { 'accessory_wheat': 3 }, exports: { 'accessory_thread': 5 } }],
        idleIntervalInTicks: 15
    },
    'settlement_vineyard': {
        key: 'settlement_vineyard',
        display: { background: '🏡', foreground: '🍇' },
        name: 'Vineyard',
        style: { backgroundColor: '#4C1D95' }, // Deep Purple
        isStationary: true,
        recipes: [{ imports: { 'accessory_water': 1 }, exports: { 'accessory_grapes': 5 } }],
        idleIntervalInTicks: 14
    },
    // --- Tier 4: Advanced Food & Goods Production ---
    'settlement_bakery': {
        key: 'settlement_bakery',
        display: { background: '🏠', foreground: '🥖' },
        name: 'Bakery',
        style: { backgroundColor: '#F59E0B' },
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1, 'accessory_wheat': 1 }, exports: { 'accessory_bread': 5 } },
            { imports: { 'accessory_bread': 1, 'accessory_meat': 1 }, exports: { 'accessory_sandwich': 5 } },
            { imports: { 'accessory_wheat': 1, 'accessory_milk': 1, 'accessory_egg': 1 }, exports: { 'accessory_croissant': 5, 'accessory_waffle': 5 } },
            { imports: { 'accessory_wheat': 1, 'accessory_milk': 1, 'accessory_egg': 1 }, exports: { 'accessory_pancakes': 5 } },
            { imports: { 'accessory_wheat': 2, 'accessory_milk': 1, 'accessory_honey': 1 }, exports: { 'accessory_doughnut': 5 } },
            { imports: { 'accessory_wheat': 2, 'accessory_apple': 2 }, exports: { 'accessory_pie': 5 } },
            { imports: { 'accessory_milk': 1, 'accessory_wheat': 1 }, exports: { 'accessory_cookie': 5 } },
            { imports: { 'accessory_wheat': 1, 'accessory_milk': 1, 'accessory_chocolate': 1 }, exports: { 'accessory_cupcake': 5 } },
            { imports: { 'accessory_water': 1, 'accessory_wheat': 1 }, exports: { 'accessory_baguette': 5 } },
            { imports: { 'accessory_water': 1, 'accessory_wheat': 1 }, exports: { 'accessory_pretzel': 5 } }
        ],
        idleIntervalInTicks: 15
    },
    'settlement_butcher_shop': {
        key: 'settlement_butcher_shop',
        display: { background: '🏠', foreground: '🔪' },
        name: 'Butcher Shop',
        style: { backgroundColor: '#991B1B' }, // Dark Red
        isStationary: true,
        recipes: [
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_bacon': 5 } }
        ],
        idleIntervalInTicks: 12
    },
    'settlement_hotdog_shop': {
        key: 'settlement_hotdog_shop',
        display: { background: '🏠', foreground: '🌭' },
        name: 'Hot Dog Shop',
        style: { backgroundColor: '#991B1B' }, // Dark Red
        isStationary: true,
        recipes: [
            { imports: { 'accessory_meat': 1, 'accessory_bread': 1 }, exports: { 'accessory_hotdog': 5 } }
        ],
        idleIntervalInTicks: 12
    },
    'settlement_pizzeria': {
        key: 'settlement_pizzeria',
        display: { background: '🏠', foreground: '🍕' },
        name: 'Pizzeria',
        style: { backgroundColor: '#DC2626' }, // Red
        isStationary: true,
        recipes: [{ imports: { 'accessory_bread': 1, 'accessory_cheese': 1, 'accessory_water': 1 }, exports: { 'accessory_pizza': 12 } }],
        idleIntervalInTicks: 20
    },
    'settlement_taco_stand': {
        key: 'settlement_taco_stand',
        display: { background: '🏠', foreground: '🌮' },
        name: 'Taco Stand',
        style: { backgroundColor: '#F59E0B' }, // Amber
        isStationary: true,
        recipes: [{ imports: { 'accessory_corn': 1, 'accessory_meat': 1, 'accessory_cheese': 1 }, exports: { 'accessory_taco': 5 } }],
        idleIntervalInTicks: 16
    },
    'settlement_sushi_bar': {
        key: 'settlement_sushi_bar',
        display: { background: '🏠', foreground: '🍣' },
        name: 'Sushi Bar',
        style: { backgroundColor: '#1E40AF' }, // Dark Blue
        isStationary: true,
        recipes: [{ imports: { 'accessory_fish': 1, 'accessory_wheat': 1 }, exports: { 'accessory_sushi': 5 } }],
        idleIntervalInTicks: 18
    },
    'settlement_milkshake_bar': {
        key: 'settlement_milkshake_bar',
        display: { background: '🏠', foreground: '🥤' },
        name: 'Milkshake Bar',
        style: { backgroundColor: '#EC4899' }, // Pink
        isStationary: true,
        recipes: [{ imports: { 'accessory_milk': 1, 'accessory_ice_cream': 1, 'accessory_strawberry': 1 }, exports: { 'accessory_milkshake': 5 } }],
        idleIntervalInTicks: 15
    },
    'settlement_burger_bar': {
        key: 'settlement_burger_bar',
        display: { background: '🏠', foreground: '🍔' },
        name: 'Burger Bar',
        style: { backgroundColor: '#F97316' }, // Orange
        isStationary: true,
        recipes: [{ imports: { 'accessory_bread': 1, 'accessory_meat': 1, 'accessory_cheese': 1 }, exports: { 'accessory_burger': 5 } }],
        idleIntervalInTicks: 22
    },
    'settlement_restaurant': {
        key: 'settlement_restaurant',
        display: { background: '🏠', foreground: '🍲' },
        name: 'Restaurant',
        style: { backgroundColor: '#F97316' }, // Orange
        isStationary: true,
        recipes: [
            { imports: { 'accessory_meat': 2, 'accessory_potato': 2 }, exports: { 'accessory_fine_meal': 1 } },
            { imports: { 'accessory_cucumber': 1, 'accessory_tomato': 1 }, exports: { 'accessory_salad': 5 } },
            { imports: { 'accessory_meat': 1, 'accessory_wheat': 1 }, exports: { 'accessory_dumpling': 5 } },
            { imports: { 'accessory_fish': 1, 'accessory_oil_drum': 1 }, exports: { 'accessory_fried_shrimp': 5 } },
            { imports: { 'accessory_rice': 1, 'accessory_hot_pepper': 1 }, exports: { 'accessory_curry': 5 } },
            { imports: { 'accessory_wheat': 2, 'accessory_tomato': 1 }, exports: { 'accessory_spaghetti': 5 } },
            { imports: { 'accessory_potato': 2, 'accessory_oil_drum': 1 }, exports: { 'accessory_fries': 5 } },
            { imports: { 'accessory_milk': 1, 'accessory_egg': 1 }, exports: { 'accessory_custard': 5 } },
            { imports: { 'accessory_cake': 1, 'accessory_candle': 1 }, exports: { 'accessory_birthday_cake': 5 } },
            { imports: { 'accessory_ice_cream': 1 }, exports: { 'accessory_ice_cream_bowl': 5 } },
            { imports: { 'accessory_water': 1, 'accessory_ice_cream': 1 }, exports: { 'accessory_shaved_ice': 5 } },
            { imports: { 'accessory_rice': 1 }, exports: { 'accessory_dango': 5 } },
            { imports: { 'accessory_fish': 1, 'accessory_rice': 1 }, exports: { 'accessory_fish_cake': 5 } },
            { imports: { 'accessory_fish_cake': 2, 'accessory_egg': 1 }, exports: { 'accessory_oden': 5 } },
            { imports: { 'accessory_potato': 1 }, exports: { 'accessory_roasted_sweet_potato': 1 } },
            { imports: { 'accessory_wheat': 1, 'accessory_meat': 1 }, exports: { 'accessory_steaming_bowl': 5 } },
            { imports: { 'accessory_rice': 1 }, exports: { 'accessory_rice_ball': 5 } },
            { imports: { 'accessory_rice': 1 }, exports: { 'accessory_rice_cracker': 5 } },
            { imports: { 'accessory_rice': 1, 'accessory_fish': 1, 'accessory_egg': 1 }, exports: { 'accessory_bento_box': 5 } },
            { imports: { 'accessory_milk': 1, 'accessory_wheat': 1 }, exports: { 'accessory_cheese': 5 } },
            { imports: { 'accessory_milk': 1, 'accessory_wheat': 1, 'accessory_strawberry': 1 }, exports: { 'accessory_cake': 5 } },
            { imports: { 'accessory_honey': 1 }, exports: { 'accessory_lollipop': 5 } },
            { imports: { 'accessory_milk': 1 }, exports: { 'accessory_ice_cream': 5 } },
            { imports: { 'accessory_milk': 1, 'accessory_honey': 1 }, exports: { 'accessory_chocolate': 5 } },
            { imports: { 'accessory_honey': 1 }, exports: { 'accessory_candy': 5 } },
            { imports: { 'accessory_corn': 1, 'accessory_oil_drum': 1 }, exports: { 'accessory_popcorn': 5 } }
        ],
        idleIntervalInTicks: 25
    },
    'settlement_tailor': {
        key: 'settlement_tailor',
        display: { background: '🏠', foreground: '👕' },
        name: 'Tailor Shop',
        style: { backgroundColor: '#60A5FA' }, // Blue-400
        isStationary: true,
        recipes: [
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_tshirt': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_jeans': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_dress': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_coat': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_kimono': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_socks': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_scarf': 1 } },
            { imports: { 'accessory_meat': 1, 'accessory_thread': 1 }, exports: { 'accessory_shoe': 1 } }, // Meat for leather
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_gloves': 1 } }
        ],
        idleIntervalInTicks: 20
    },
    // --- Tier 5: Specialty & Luxury Goods ---
    'settlement_jeweler': {
        key: 'settlement_jeweler',
        display: { background: '💎', foreground: '💍' },
        name: 'Jeweler',
        style: { backgroundColor: '#6D28D9' }, // Purple
        isStationary: true,
        recipes: [
            { imports: { 'accessory_gem': 1, 'accessory_hammer': 1 }, exports: { 'accessory_ring': 1 } },
            { imports: { 'accessory_gem': 3, 'accessory_money_bag': 2, 'accessory_ring': 1 }, exports: { 'accessory_crown': 1 } },
            { imports: { 'accessory_gem': 1, 'accessory_clock': 1 }, exports: { 'accessory_watch': 1 } },
            { imports: { 'accessory_gem': 2 }, exports: { 'accessory_glasses': 1 } },
            { imports: { 'accessory_gem': 2, 'accessory_stone': 1 }, exports: { 'accessory_sunglasses': 1 } }
        ],
        idleIntervalInTicks: 25
    },
    'settlement_winery': {
        key: 'settlement_winery',
        display: { background: '🏠', foreground: '🍷' },
        name: 'Winery',
        style: { backgroundColor: '#581C87' }, // Dark Purple
        isStationary: true,
        recipes: [{ imports: { 'accessory_grapes': 3 }, exports: { 'accessory_wine': 1 } }],
        idleIntervalInTicks: 30
    },
    'settlement_brewery': {
        key: 'settlement_brewery',
        display: { background: '🏭', foreground: '🍺' },
        name: 'Brewery',
        style: { backgroundColor: '#F59E0B' }, // Amber
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1, 'accessory_wheat': 1 }, exports: { 'accessory_beer': 5 } },
            { imports: { 'accessory_grapes': 5 }, exports: { 'accessory_bottle_with_cork': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_tumbler_glass': 1 } },
            { imports: { 'accessory_tumbler_glass': 2 }, exports: { 'accessory_clinking_glasses': 1 } },
            { imports: { 'accessory_beer': 2 }, exports: { 'accessory_clinking_beer_mugs': 1 } },
            { imports: { 'accessory_fruit': 2, 'accessory_water': 1 }, exports: { 'accessory_tropical_drink': 1 } },
            { imports: { 'accessory_milk': 1 }, exports: { 'accessory_baby_bottle': 1 } },
            { imports: { 'accessory_grapes': 2, 'accessory_water': 1 }, exports: { 'accessory_sake': 1 } },
            { imports: { 'accessory_tropical_drink': 1, 'accessory_alcohol': 1 }, exports: { 'accessory_cocktail': 1 } },
            { imports: { 'accessory_leaf': 1, 'accessory_water': 1 }, exports: { 'accessory_tea': 1 } }
        ],
        idleIntervalInTicks: 18
    },
    'settlement_coffee_shop': {
        key: 'settlement_coffee_shop',
        display: { background: '🏠', foreground: '☕' },
        name: 'Coffee Shop',
        style: { backgroundColor: '#4A2C2A' }, // Coffee Brown
        isStationary: true,
        recipes: [{ imports: { 'accessory_water': 1 }, exports: { 'accessory_cup': 1 } }],
        idleIntervalInTicks: 8
    },
    'settlement_armory': {
        key: 'settlement_armory',
        display: { background: '🛡️', foreground: '⚔️' },
        name: 'Armory',
        style: { backgroundColor: '#4B5563' }, // Dark Gray
        isStationary: true,
        recipes: [
            { imports: { 'accessory_stone': 3, 'accessory_wood': 1 }, exports: { 'accessory_trident': 1 } },
            { imports: { 'accessory_stone': 2, 'accessory_wood': 1 }, exports: { 'accessory_dagger': 1 } },
            { imports: { 'accessory_stone': 3, 'accessory_wood': 2 }, exports: { 'accessory_crossed_swords': 1 } },
            { imports: { 'accessory_stone': 2, 'accessory_wood': 2 }, exports: { 'accessory_bomb': 1 } },                
            { imports: { 'accessory_wood': 3, 'accessory_thread': 2 }, exports: { 'accessory_bow_and_arrow': 1 } }
        ],
        idleIntervalInTicks: 30
    },
    'settlement_mint': {
        key: 'settlement_mint',
        display: { background: '🏦', foreground: '🏅' },
        name: 'Mint',
        style: { backgroundColor: '#FBBF24' }, // Amber
        isStationary: true,
        recipes: [
            { imports: { 'accessory_coin': 10 }, exports: { 'accessory_bronze_medal': 1 } },
            { imports: { 'accessory_coin': 10 }, exports: { 'accessory_silver_medal': 1 } },
            { imports: { 'accessory_coin': 10 }, exports: { 'accessory_gold_medal': 1 } },
            { imports: { 'accessory_gold_medal': 1 }, exports: { 'accessory_medal': 1 } },
            { imports: { 'accessory_medal': 2 }, exports: { 'accessory_military_medal': 1 } },
            { imports: { 'accessory_gold_medal': 1 }, exports: { 'accessory_trophy': 1 } }
        ],
        idleIntervalInTicks: 40
    },
    'settlement_florist': {
        key: 'settlement_florist',
        display: { background: '💐', foreground: '🌹' },
        name: 'Florist',
        style: { backgroundColor: '#EC4899' }, // Pink
        isStationary: true,
        recipes: [
            { imports: { 'accessory_water': 1 }, exports: { 'accessory_flower': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_rose': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_gift': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_balloon': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_jackolantern': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_pine_decoration': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_tanabata_tree': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_party_popper': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_reminder_ribbon': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_ribbon': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_moon_viewing_ceremony': 1 } },
            { imports: { 'accessory_flower': 1 }, exports: { 'accessory_japanese_dolls': 1 } }
        ],
        idleIntervalInTicks: 15
    },
    'settlement_pharmacy': {
        key: 'settlement_pharmacy',
        display: { background: '🏥', foreground: '💊' },
        name: 'Pharmacy',
        style: { backgroundColor: '#EF4444' }, // Red
        isStationary: true,
        recipes: [
            { imports: { 'accessory_wheat': 1 }, exports: { 'accessory_cigarette': 5 } },
            { imports: { 'accessory_water': 1, 'accessory_mushroom': 1 }, exports: { 'accessory_pill': 5 } }
        ],
        idleIntervalInTicks: 20
    },
    // --- Tier 6: Utility & Storage ---
    'settlement_warehouse': {
        key: 'settlement_warehouse',
        display: { background: '🏦', foreground: '📦' },
        name: 'Warehouse',
        style: { backgroundColor: '#A1A1AA' },
        isStationary: true,
        isUniversal: true // This settlement can import/export any item.
    },
     'settlement_recycler': {
        key: 'settlement_recycler',
        display: { background: '♻️', foreground: '🗑️' },
        name: 'Recycling Center',
        style: { backgroundColor: '#4B5563' }, // Gray-600
        isStationary: true,
        // This settlement accepts any item that doesn't have a defined action in npcActionsConfig.
        // The logic for this is handled in SettlementManager.
        acceptsNoActionItems: true,
        recipes: [{
            exports: { 'accessory_coin': 1 } // The output for any recycled item.
        }],
        idleIntervalInTicks: 5
    },
    'settlement_bank': {
        key: 'settlement_bank',
        display: { background: '💰', foreground: '💵' },
        name: 'Bank',
        style: { backgroundColor: '#FBBF24' }, // Amber
        isStationary: true,
        isBank: true, // Special flag to control money item pickup logic
        recipes: [
            { imports: { 'accessory_coin': 5 }, exports: { 'accessory_dollar': 1 } },
            { imports: { 'accessory_coin': 5 }, exports: { 'accessory_euro': 1 } },
            { imports: { 'accessory_coin': 5 }, exports: { 'accessory_pound': 1 } },
            { imports: { 'accessory_coin': 5 }, exports: { 'accessory_yen': 1 } },
            { imports: { 'accessory_dollar': 5 }, exports: { 'accessory_money_bag': 1 } },
            { imports: { 'accessory_euro': 5 }, exports: { 'accessory_money_bag': 1 } },
            { imports: { 'accessory_pound': 5 }, exports: { 'accessory_money_bag': 1 } },
            { imports: { 'accessory_yen': 5 }, exports: { 'accessory_money_bag': 1 } },
            { imports: { 'accessory_money_bag': 5 }, exports: { 'accessory_creditcard': 1 } }
        ],
        idleIntervalInTicks: 10
    },
    // --- Tier 7: Advanced Production ---
    'settlement_factory': {
        key: 'settlement_factory',
        display: { background: '🏭', foreground: '⚙️' },
        name: 'Factory',
        style: { backgroundColor: '#4B5563' }, // Dark Gray
        isStationary: true,
        recipes: [
            // Raw materials
            { imports: { 'accessory_stone': 2 }, exports: { 'accessory_oil_drum': 1 } }, // Represents drilling for oil
            { imports: { 'accessory_stone': 1, 'accessory_wood': 1 }, exports: { 'accessory_gear': 2 } }, // Represents smelting ore to make gears
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_nut_and_bolt': 3 } }, // Represents smelting ore to make nuts and bolts
            { imports: { 'accessory_water': 1, 'accessory_gem': 1 }, exports: { 'accessory_potion': 1 } }, // Represents chemical synthesis

            // Components
            { imports: { 'accessory_nut_and_bolt': 2, 'accessory_potion': 1 }, exports: { 'accessory_battery': 1 } },
            { imports: { 'accessory_oil_drum': 1, 'accessory_nut_and_bolt': 1 }, exports: { 'accessory_computer_disk': 1 } }, // Plastic + metal
            { imports: { 'accessory_gem': 1, 'accessory_nut_and_bolt': 1 }, exports: { 'accessory_light_bulb': 1 } }, // Glass + filament

            // Simple Electronics
            { imports: { 'accessory_computer_disk': 1, 'accessory_battery': 1 }, exports: { 'accessory_pager': 1 } },
            { imports: { 'accessory_computer_disk': 1, 'accessory_gear': 2, 'accessory_nut_and_bolt': 2 }, exports: { 'accessory_telephone': 1 } },
            { imports: { 'accessory_telephone': 1, 'accessory_gear': 1 }, exports: { 'accessory_telephone_receiver': 1 } },
            { imports: { 'accessory_telephone': 1, 'accessory_printer': 1 }, exports: { 'accessory_fax_machine': 1 } },

            // Complex Electronics
            { imports: { 'accessory_oil_drum': 1, 'accessory_nut_and_bolt': 2 }, exports: { 'accessory_keyboard': 1 } },
            { imports: { 'accessory_oil_drum': 1, 'accessory_gear': 1 }, exports: { 'accessory_computer_mouse': 1 } },
            { imports: { 'accessory_light_bulb': 1, 'accessory_gear': 2, 'accessory_plank': 1 }, exports: { 'accessory_television': 1 } },
            { imports: { 'accessory_gear': 2, 'accessory_nut_and_bolt': 4, 'accessory_computer_disk': 1 }, exports: { 'accessory_printer': 1 } },
            { imports: { 'accessory_keyboard': 1, 'accessory_computer_mouse': 1, 'accessory_television': 1, 'accessory_computer_disk': 2 }, exports: { 'accessory_desktop_computer': 1 } },
            { imports: { 'accessory_desktop_computer': 1, 'accessory_battery': 1 }, exports: { 'accessory_laptop': 1 } },
            { imports: { 'accessory_telephone': 1, 'accessory_computer_disk': 1, 'accessory_battery': 1 }, exports: { 'accessory_phone': 1 } },

            // Media
            { imports: { 'accessory_computer_disk': 1 }, exports: { 'accessory_floppy_disk': 1 } },
            { imports: { 'accessory_computer_disk': 1, 'accessory_gem': 1 }, exports: { 'accessory_cd': 1 } },
            { imports: { 'accessory_cd': 1, 'accessory_oil_drum': 1 }, exports: { 'accessory_dvd': 1 } },

            // Cameras
            { imports: { 'accessory_gear': 1, 'accessory_nut_and_bolt': 1, 'accessory_gem': 1 }, exports: { 'accessory_camera': 1 } },
            { imports: { 'accessory_camera': 1, 'accessory_light_bulb': 1 }, exports: { 'accessory_camera_with_flash': 1 } },
            { imports: { 'accessory_camera_with_flash': 1, 'accessory_computer_disk': 1 }, exports: { 'accessory_video_camera': 1 } },
            { imports: { 'accessory_video_camera': 1, 'accessory_gear': 2 }, exports: { 'accessory_movie_camera': 1 } },
            { imports: { 'accessory_movie_camera': 1, 'accessory_light_bulb': 1 }, exports: { 'accessory_film_projector': 1 } },

            // Science equipment
            { imports: { 'accessory_water': 1, 'accessory_gem': 1 }, exports: { 'accessory_alembic': 1 } },
            { imports: { 'accessory_gem': 2 }, exports: { 'accessory_crystal_ball': 1 } },
            { imports: { 'accessory_gem': 1, 'accessory_gear': 2 }, exports: { 'accessory_telescope': 1 } },
            { imports: { 'accessory_telescope': 1, 'accessory_gem': 1 }, exports: { 'accessory_microscope': 1 } }
        ],
        idleIntervalInTicks: 35
    },
    'settlement_library': {
        key: 'settlement_library',
        display: { background: '📚', foreground: '📝' },
        name: 'Library',
        style: { backgroundColor: '#A16207' }, // Brown
        isStationary: true,
        recipes: [
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_card_index': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_spiral_calendar': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_memo': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_e-mail': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_bookmark_tabs': 1 } },
            { imports: { 'accessory_paper': 2 }, exports: { 'accessory_rolled-up_newspaper': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_page_with_curl': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_page_facing_up': 1 } },
            { imports: { 'accessory_page_facing_up': 5 }, exports: { 'accessory_ledger': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_notebook': 1 } },
            { imports: { 'accessory_notebook': 3 }, exports: { 'accessory_books': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_orange_book': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_blue_book': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_green_book': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_closed_book': 1 } },
            { imports: { 'accessory_notebook': 1 }, exports: { 'accessory_notebook_with_decorative_cover': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_admission_tickets': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_book': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_scroll': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_map': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_ticket': 1 } },
            { imports: { 'accessory_books': 1 }, exports: { 'accessory_briefcase': 1 } },
            { imports: { 'accessory_rolled-up_newspaper': 1 }, exports: { 'accessory_newspaper': 1 } },
            { imports: { 'accessory_paper': 1 }, exports: { 'accessory_envelope': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_paperclip': 5 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_pushpin': 5 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_pen': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_pencil': 1 } },
            { imports: { 'accessory_pen': 1, 'accessory_gem': 1 }, exports: { 'accessory_fountain_pen': 1 } },
            { imports: { 'accessory_honey': 1 }, exports: { 'accessory_crayon': 3 } },
            { imports: { 'accessory_plank': 1, 'accessory_paperclip': 1 }, exports: { 'accessory_clipboard': 1 } },
            { imports: { 'accessory_plank': 1 }, exports: { 'accessory_triangularruler': 1 } },
            { imports: { 'accessory_plank': 1 }, exports: { 'accessory_ruler': 1 } }
        ],
        idleIntervalInTicks: 20
    },
    'settlement_toy_shop': {
        key: 'settlement_toy_shop',
        display: { background: '🧸', foreground: '🎁' },
        name: 'Toy Shop',
        style: { backgroundColor: '#EC4899' }, // Pink
        isStationary: true,
        recipes: [
            { imports: { 'accessory_paper': 2 }, exports: { 'accessory_flower_playing_cards': 1 } },
            { imports: { 'accessory_flower_playing_cards': 1 }, exports: { 'accessory_joker': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_toy_car': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_toy_truck': 1 } },
            { imports: { 'accessory_thread': 2 }, exports: { 'accessory_teddy_bear': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_toy_train': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_gun': 1 } },
            { imports: { 'accessory_gear': 1 }, exports: { 'accessory_joystick': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_mahjong': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_die': 2 } }
        ],
        idleIntervalInTicks: 20
    },
    'settlement_studio': {
        key: 'settlement_studio',
        display: { background: '🎸', foreground: '🎤' },
        name: 'Music & Art Studio',
        style: { backgroundColor: '#6D28D9' }, // Purple
        isStationary: true,
        recipes: [
            { imports: { 'accessory_gear': 1 }, exports: { 'accessory_radio': 1 } },
            { imports: { 'accessory_radio': 1 }, exports: { 'accessory_headphones': 1 } },
            { imports: { 'accessory_gear': 1 }, exports: { 'accessory_studio_microphone': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_postal_horn': 1 } },
            { imports: { 'accessory_gear': 1 }, exports: { 'accessory_loudspeaker': 1 } },
            { imports: { 'accessory_loudspeaker': 1 }, exports: { 'accessory_megaphone': 1 } },
            { imports: { 'accessory_studio_microphone': 1 }, exports: { 'accessory_microphone': 1 } },
            { imports: { 'accessory_wood': 1, 'accessory_thread': 3 }, exports: { 'accessory_guitar': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_trumpet': 1 } },
            { imports: { 'accessory_wood': 1, 'accessory_thread': 4 }, exports: { 'accessory_violin': 1 } },
            { imports: { 'accessory_wood': 1, 'accessory_meat': 1 }, exports: { 'accessory_drum': 1 } },
            { imports: { 'accessory_wood': 1, 'accessory_thread': 1 }, exports: { 'accessory_artist_palette': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_clapper_board': 1 } }
        ],
        idleIntervalInTicks: 25
    },
    'settlement_sports_complex': {
        key: 'settlement_sports_complex',
        display: { background: '🏟️', foreground: '⚽' },
        name: 'Sports Complex',
        style: { backgroundColor: '#16A34A' }, // Green
        isStationary: true,
        recipes: [
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_soccerball': 1 } },
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_baseball': 1 } },
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_basketball': 1 } },
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_volleyball': 1 } },
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_football': 1 } },
            { imports: { 'accessory_meat': 1 }, exports: { 'accessory_rugby': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_tennis': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_8ball': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_pingpong': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_badminton': 1 } },
            { imports: { 'accessory_meat': 1, 'accessory_thread': 1 }, exports: { 'accessory_boxingglove': 1 } }
        ],
        idleIntervalInTicks: 15
    },
    'settlement_general_store': {
        key: 'settlement_general_store',
        display: { background: '🏬', foreground: '🛒' },
        name: 'General Store',
        style: { backgroundColor: '#4682B4' },
        isStationary: true,
        recipes: [
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_locked': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_wastebasket': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_card_file_box': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_bellhop_bell': 1 } },
            { imports: { 'accessory_gem': 2 }, exports: { 'accessory_hourglass_done': 1 } },
            { imports: { 'accessory_hourglass_done': 1 }, exports: { 'accessory_mantelpiece_clock': 1 } },
            { imports: { 'accessory_mantelpiece_clock': 1 }, exports: { 'accessory_alarm_clock': 1 } },
            { imports: { 'accessory_gear': 2 }, exports: { 'accessory_shopping_cart': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_purse': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_umbrella': 1 } },
            { imports: { 'accessory_honey': 1 }, exports: { 'accessory_lipstick': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_top_hat': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_funeral_urn': 1 } },
            { imports: { 'accessory_wood': 1 }, exports: { 'accessory_framed_picture': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_statue_of_liberty': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_tokyo_tower': 1 } },
            { imports: { 'accessory_stone': 1 }, exports: { 'accessory_moyai': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_chequeredflag': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_triangularflag': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_blackflag': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_whiteflag': 1 } },
            { imports: { 'accessory_thread': 1 }, exports: { 'accessory_rainbowflag': 1 } },
            { imports: { 'accessory_light_bulb': 1 }, exports: { 'accessory_lantern': 1 } },
            { imports: { 'accessory_honey': 1 }, exports: { 'accessory_candle': 1 } },
            { imports: { 'accessory_battery': 1 }, exports: { 'accessory_flashlight': 1 } }
        ],
        idleIntervalInTicks: 10
    }
};